import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { movementTypeLabel } from "../../lib/movementTypes";
import { PhotoGallery } from "../../components/photo/PhotoGallery";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TYPES = ["all", "in", "out", "relocate", "adjustment", "loan", "return", "disposal", "pick", "lock", "unlock", "maintenance_in", "maintenance_out"];

export default function Verlauf() {
    const [items, setItems] = useState([]);
    const [type, setType] = useState("all");
    const [loading, setLoading] = useState(true);
    // #118: Scan-Folgeaktion "Verlauf anzeigen" übergibt ?article=<id>.
    const [searchParams, setSearchParams] = useSearchParams();
    const articleId = searchParams.get("article") || "";
    const [articleName, setArticleName] = useState("");

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams({ limit: "100" });
        if (type && type !== "all") params.set("type", type);
        if (articleId) params.set("article_id", articleId);
        rpc.get(`/movements?${params.toString()}`).then((r) => {
            if (r.ok) setItems(r.data);
            setLoading(false);
        });
    }, [type, articleId]);

    useEffect(() => {
        if (!articleId) {
            setArticleName("");
            return;
        }
        rpc.get("/master/articles").then((r) => {
            if (r.ok) setArticleName(r.data.find((a) => a.id === articleId)?.name || "");
        });
    }, [articleId]);

    return (
        <section className="flex flex-col gap-4" data-testid="verlauf-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Floor Mode
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Verlauf</h1>
            </div>
            <Tabs defaultValue="movements">
                <TabsList className="w-full">
                    <TabsTrigger value="movements" className="flex-1" data-testid="verlauf-tab-movements">Buchungen</TabsTrigger>
                    <TabsTrigger value="photos" className="flex-1" data-testid="verlauf-tab-photos">Meine Fotos</TabsTrigger>
                </TabsList>
                <TabsContent value="movements" className="mt-4 flex flex-col gap-4">
                    <Select value={type} onValueChange={setType}>
                        <SelectTrigger className="h-12" data-testid="verlauf-type-filter">
                            <SelectValue placeholder="Typ filtern" />
                        </SelectTrigger>
                        <SelectContent>
                            {TYPES.map((t) => (
                                <SelectItem key={t} value={t}>
                                    {t === "all" ? "alle" : movementTypeLabel(t)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {articleId ? (
                        <div
                            className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-xs"
                            data-testid="verlauf-article-filter"
                        >
                            <span className="flex-1 truncate">
                                Nur Artikel: <span className="font-medium">{articleName || articleId.slice(0, 8)}</span>
                            </span>
                            <button
                                type="button"
                                onClick={() => setSearchParams({})}
                                aria-label="Artikel-Filter aufheben"
                                className="pw-tap rounded-md border border-border px-2 py-1"
                                data-testid="verlauf-article-filter-clear"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ) : null}
                    <div className="flex flex-col gap-2" data-testid="verlauf-list">
                        {loading ? (
                            <div className="text-xs text-muted-foreground">laden...</div>
                        ) : items.length === 0 ? (
                            <div className="rounded-lg border border-border/60 bg-card/40 px-3 py-4 text-center text-xs text-muted-foreground">
                                Keine Einträge.
                            </div>
                        ) : (
                            items.map((m) => (
                                <div
                                    key={m.id}
                                    className="rounded-lg border border-border/60 bg-card/40 px-3 py-2 text-xs"
                                    data-testid="verlauf-row"
                                    data-movement-id={m.id}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-primary font-semibold uppercase tracking-wide">
                                            {movementTypeLabel(m.type)}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {new Date(m.created_at).toLocaleString("de-DE")}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-foreground">
                                        Menge: {m.quantity ?? "—"} · Vorher {m.previous_quantity ?? "—"} → Nachher {m.new_quantity ?? "—"}
                                    </div>
                                    {m.comment ? (
                                        <div className="mt-1 text-muted-foreground">„{m.comment}"</div>
                                    ) : null}
                                </div>
                            ))
                        )}
                    </div>
                </TabsContent>
                <TabsContent value="photos" className="mt-4">
                    <PhotoGallery
                        showFilters={true}
                        isAdmin={false}
                        testId="verlauf-photos"
                    />
                </TabsContent>
            </Tabs>
        </section>
    );
}
